import { Component, OnInit } from '@angular/core';
import { DashboardMesaControlService } from '../../services/mesa-control/mesa-control.service';
import swal2 from 'sweetalert2';
import * as ExcelJS from 'exceljs/dist/exceljs.min.js';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-mesa-control',
  templateUrl: './mesa-control.component.html',
  styleUrls: ['./mesa-control.component.css']
})
export class DashboardMesaControlComponent implements OnInit {

  fechaConsulta: string;
  operaciones: any[] = [];
  banxicoRates: any = null;
  loading = false;

  // Tabla tasas por cadena y banco (endpoint company_bank_rates)
  companyBankRates: any[] = [];
  companyBankRatesFiltradas: any[] = [];
  companyBancosList: string[] = [];

  // KPIs del grid
  totalOperaciones = 0;
  opsCorrectos = 0;
  opsDiscrepancias = 0;

  constructor(private mesaControlService: DashboardMesaControlService) {}

  ngOnInit() {
    const today = new Date();
    this.fechaConsulta = this.formatDate(today);

    // Cargar tabla cadenas-bancos (no depende de fecha, una sola vez)
    this.cargarCompanyBankRates();
  }

  // ==================== NAVEGACIÓN DE FECHA ====================
  cambiarFecha(dias: number) {
    if (!this.fechaConsulta) { return; }
    const parts = this.fechaConsulta.split('-');
    const fecha = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    fecha.setDate(fecha.getDate() + dias);
    this.fechaConsulta = this.formatDate(fecha);
    this.consultar();
  }

  formatDate(d: Date): string {
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();
    if (month.length < 2) { month = '0' + month; }
    if (day.length < 2) { day = '0' + day; }
    return [year, month, day].join('-');
  }

  // ==================== CONSULTAR ====================
  consultar() {
    if (!this.fechaConsulta) {
      swal2.fire('Error', 'Seleccione una fecha', 'error');
      return;
    }

    this.loading = true;
    swal2.fire({ title: 'Cargando datos...', allowOutsideClick: false });
    swal2.showLoading();

    let completados = 0;
    const totalLlamadas = 2;
    const errores = [];

    const checkComplete = () => {
      completados++;
      if (completados >= totalLlamadas) {
        this.loading = false;
        swal2.close();
        if (errores.length > 0) {
          swal2.fire('Advertencia', 'Algunos datos no pudieron cargarse: ' + errores.join(', '), 'warning');
        }
        if (this.operaciones.length === 0) {
          swal2.fire('Sin datos', 'No se encontraron operaciones para la fecha: ' + this.fechaConsulta, 'info');
        }
        this.calcularKPIs();
      }
    };

    // 1. Reporte extendido (grid de validación)
    this.mesaControlService.getDailyOperationsExtended(this.fechaConsulta, 'SI').subscribe(
      resp => { this.operaciones = resp || []; checkComplete(); },
      err => { this.operaciones = []; errores.push('Operaciones'); checkComplete(); }
    );

    // 2. Tasas Banxico
    this.mesaControlService.getBanxicoRates(this.fechaConsulta).subscribe(
      resp => { this.banxicoRates = resp; checkComplete(); },
      err => { this.banxicoRates = null; errores.push('Tasas Banxico'); checkComplete(); }
    );
  }

  // ==================== KPIs ====================
  calcularKPIs() {
    this.totalOperaciones = this.operaciones.length;
    this.opsCorrectos = this.operaciones.filter(op => op.estado_final === true).length;
    this.opsDiscrepancias = this.operaciones.filter(op => op.estado_final === false).length;
    this.filtrarTasasPorCadena();
  }

  // ==================== FILTRAR TASAS POR CADENAS DEL DÍA ====================
  filtrarTasasPorCadena() {
    if (this.companyBankRates.length === 0 || this.operaciones.length === 0) {
      this.companyBankRatesFiltradas = this.companyBankRates;
      return;
    }
    // Obtener cadenas/deudores únicos de las operaciones del día
    const cadenasDelDia = new Set<string>();
    this.operaciones.forEach(op => {
      if (op.deudor) { cadenasDelDia.add(op.deudor.toUpperCase().trim()); }
    });
    // Filtrar solo las que coinciden
    this.companyBankRatesFiltradas = this.companyBankRates.filter(cadena =>
      cadenasDelDia.has(cadena.nombre.toUpperCase().trim())
    );
    // Si no hay match, mostrar todas como fallback
    if (this.companyBankRatesFiltradas.length === 0) {
      this.companyBankRatesFiltradas = this.companyBankRates;
    }
  }

  // ==================== CARGAR TASAS POR CADENA Y BANCO ====================
  cargarCompanyBankRates() {
    this.mesaControlService.getCompanyBankRates().subscribe(
      resp => {
        if (resp && resp.columnas_fondeo && resp.cadenas) {
          this.companyBancosList = resp.columnas_fondeo;
          this.companyBankRates = resp.cadenas;
        } else if (resp && resp.bancos && resp.cadenas) {
          // Fallback al formato anterior
          this.companyBancosList = resp.bancos;
          this.companyBankRates = resp.cadenas;
        }
      },
      err => {
        this.companyBankRates = [];
        this.companyBancosList = [];
      }
    );
  }

  // ==================== EXPORTAR EXCEL GENERAL ====================
  exportExcelGeneral() {
    if (this.operaciones.length === 0) {
      swal2.fire('Sin datos', 'No hay datos para exportar', 'warning');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Operación del día');

    const headers = [
      'No. Oper.', 'No. Control', '# facturas', 'Proveedor', 'DEUDOR',
      'Fecha salida', 'Fecha vence', 'días', 'Importe', 'Moneda',
      'TIIE', 'Sobre tasa', 'Tasa Factor', 'Interés Factor',
      'Monto Importe menos intereses', 'Monto por Disposición', 'Agrupador', '', 'BANCO'
    ];

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      cell.alignment = { horizontal: 'center' };
    });

    this.operaciones.forEach((op, index) => {
      const row = [
        index + 1,
        op.folio_solicitud || '',
        op.folio_factura || '',
        op.proveedor || '',
        op.deudor || '',
        op.fecha_operacion || '',
        op.fecha_vencimiento || '',
        op.dias_factoraje || '',
        op.importe != null ? parseFloat(op.importe) : '',
        op.moneda || '',
        op.tasa_interbancaria != null ? (parseFloat(op.tasa_interbancaria) / 100) : '',
        op.sobre_tasa != null ? (parseFloat(op.sobre_tasa) / 100) : '',
        op.tasa_factoraje != null ? (parseFloat(op.tasa_factoraje) / 100) : '',
        op.intereses_factoraje != null ? parseFloat(op.intereses_factoraje) : '',
        op.importe_sin_intereses_factoraje != null ? parseFloat(op.importe_sin_intereses_factoraje) : '',
        op.por_disposicion_solicitud || '',
        op.folio_solicitud || '',
        '',
        op.fondeador || ''
      ];
      worksheet.addRow(row);
    });

    worksheet.columns.forEach((col, i) => { col.width = i === 3 || i === 4 ? 30 : 18; });
    worksheet.getColumn(11).numFmt = '0.0000%';
    worksheet.getColumn(12).numFmt = '0.00%';
    worksheet.getColumn(13).numFmt = '0.0000%';
    worksheet.getColumn(9).numFmt = '$#,##0.00';
    worksheet.getColumn(14).numFmt = '$#,##0.00';
    worksheet.getColumn(15).numFmt = '$#,##0.00';

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      FileSaver.saveAs(blob, `FGG_Operacion_del_dia_${this.fechaConsulta}.xlsx`);
    });
  }

  // ==================== EXPORTAR EXCEL POR FONDEADOR ====================
  /**
   * Descarga el reporte Excel agrupado por fondeador.
   * Consume el endpoint dedicado daily_operations_by_funder, que agrupa sin
   * duplicar facturas y devuelve los totales por fondeador ya calculados.
   * Actualizado: 20/08/2026 (Opción A - se elimina la reagrupación local que duplicaba facturas)
   */
  exportExcelByFunder() {
    if (!this.fechaConsulta) {
      swal2.fire('Error', 'Seleccione una fecha', 'error');
      return;
    }

    swal2.fire({ title: 'Generando reporte...', allowOutsideClick: false });
    swal2.showLoading();

    this.mesaControlService.getDailyOperationsByFunder(this.fechaConsulta).subscribe(
      resp => {
        const fondeadores = resp && resp.fondeadores ? resp.fondeadores : [];
        if (fondeadores.length === 0) {
          swal2.close();
          swal2.fire('Sin datos', 'No hay operaciones por fondeador para exportar', 'warning');
          return;
        }
        this.construirExcelPorFondeador(fondeadores, resp.resumen);
        swal2.close();
      },
      err => {
        swal2.close();
        swal2.fire('Error', 'No se pudo generar el reporte por fondeador', 'error');
      }
    );
  }

  /**
   * Construye el archivo Excel por fondeador a partir de la respuesta agrupada
   * del backend (una pestaña por fondeador). Los totales se toman de los campos
   * total_* que devuelve el endpoint, no se recalculan en cliente.
   * Agregado: 20/08/2026
   * Actualizado: 20/08/2026 (usa total_neto_fondeo/total_neto_general del backend + hoja Resumen)
   */
  private construirExcelPorFondeador(fondeadores: any[], resumen?: any) {
    const workbook = new ExcelJS.Workbook();

    // Hoja Resumen con totales generales tomados del backend
    if (resumen) {
      const resumenSheet = workbook.addWorksheet('Resumen');
      const resHeader = resumenSheet.addRow(['Concepto', 'Total']);
      resHeader.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
        cell.alignment = { horizontal: 'center' };
      });
      resumenSheet.addRow(['Total de fondeadores', resumen.total_fondeadores != null ? resumen.total_fondeadores : '']);
      resumenSheet.addRow(['Total de facturas', resumen.total_facturas_general != null ? resumen.total_facturas_general : '']);
      const rowOperado = resumenSheet.addRow(['Total operado', resumen.total_operado_general != null ? parseFloat(resumen.total_operado_general) : '']);
      const rowIntereses = resumenSheet.addRow(['Total intereses', resumen.total_intereses_general != null ? parseFloat(resumen.total_intereses_general) : '']);
      const rowNeto = resumenSheet.addRow(['Total neto', resumen.total_neto_general != null ? parseFloat(resumen.total_neto_general) : '']);
      resumenSheet.getColumn(1).width = 26;
      resumenSheet.getColumn(2).width = 20;
      // Formato moneda solo en las filas de importes (no en los conteos)
      [rowOperado, rowIntereses, rowNeto].forEach(r => { r.getCell(2).numFmt = '$#,##0.00'; });
    }

    const funderHeaders = [
      'Disposición', '# facturas', 'Proveedor', 'DEUDOR',
      'Fecha salida', 'Fecha vence', 'días', 'Importe', 'Moneda',
      'TIIE DE DÍA', 'Sobre tasa', 'Tasa', 'Interés', 'Monto Importe menos intereses'
    ];

    fondeadores.forEach(fondeador => {
      const nombreFondeador = fondeador.fondeador || 'Sin Fondeador';
      const sheetName = nombreFondeador.substring(0, 31);
      const worksheet = workbook.addWorksheet(sheetName);

      const headerRow = worksheet.addRow(funderHeaders);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
        cell.alignment = { horizontal: 'center' };
      });

      const facturas = fondeador.facturas || [];
      facturas.forEach(f => {
        worksheet.addRow([
          f.folio_solicitud_fondeo || '',
          f.folio_factura || '',
          f.proveedor || '',
          f.deudor || '',
          f.fecha_operacion || '',
          f.fecha_vencimiento || '',
          f.dias_fondeo || '',
          f.importe != null ? parseFloat(f.importe) : '',
          f.moneda || '',
          f.tasa_interbancaria_fondeo != null ? (parseFloat(f.tasa_interbancaria_fondeo) / 100) : '',
          f.sobre_tasa_fondeo != null ? (parseFloat(f.sobre_tasa_fondeo) / 100) : '',
          f.tasa_total_fondeo != null ? (parseFloat(f.tasa_total_fondeo) / 100) : '',
          f.intereses_fondeo != null ? parseFloat(f.intereses_fondeo) : '',
          f.importe_sin_intereses_fondeo != null ? parseFloat(f.importe_sin_intereses_fondeo) : ''
        ]);
      });

      // Fila totales tomada directamente del backend (evita sumar duplicados)
      // total_neto_fondeo viene calculado por partida en backend (más exacto que restar en cliente)
      const totalOperado = fondeador.total_operado != null ? parseFloat(fondeador.total_operado) : 0;
      const totalInteres = fondeador.total_intereses_fondeo != null ? parseFloat(fondeador.total_intereses_fondeo) : 0;
      const totalNeto = fondeador.total_neto_fondeo != null ? parseFloat(fondeador.total_neto_fondeo) : (totalOperado - totalInteres);
      const totalesRow = worksheet.addRow([
        '', '', '', '', '', '', '', totalOperado, '', '', '', '', totalInteres, totalNeto
      ]);
      totalesRow.eachCell((cell) => { cell.font = { bold: true }; });

      worksheet.columns.forEach((col, i) => { col.width = i === 2 || i === 3 ? 30 : 18; });
      worksheet.getColumn(10).numFmt = '0.0000%';
      worksheet.getColumn(11).numFmt = '0.00%';
      worksheet.getColumn(12).numFmt = '0.00%';
      worksheet.getColumn(8).numFmt = '$#,##0.00';
      worksheet.getColumn(13).numFmt = '$#,##0.00';
      worksheet.getColumn(14).numFmt = '$#,##0.00';
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      FileSaver.saveAs(blob, `FGG_Operacion_PorFondeador_${this.fechaConsulta}.xlsx`);
    });
  }

  // ==================== FORMATOS ====================
  formatCurrency(value: any): string {
    if (value === null || value === undefined || value === '') { return '$0.00'; }
    const num = parseFloat(value);
    if (isNaN(num)) { return '$0.00'; }
    return '$' + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  formatPercent(value: any): string {
    if (value === null || value === undefined || value === '') { return '0.00%'; }
    const num = parseFloat(value);
    if (isNaN(num)) { return '0.00%'; }
    return num.toFixed(2) + '%';
  }

  formatPercentBanxico(value: any): string {
    if (value === null || value === undefined || value === '') { return '0.0000%'; }
    const num = parseFloat(value);
    if (isNaN(num)) { return '0.0000%'; }
    return num.toFixed(4) + '%';
  }
}
